import { useEffect, useState } from 'react';
import axios from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const cardStyle  = { background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' };
const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { display: 'block', marginBottom: '6px', fontWeight: '600', color: '#444' };

const ROLE_CONFIG = {
  invigilator:  { label: 'Invigilator',  color: '#1565c0', bg: '#e3f2fd', border: '#90caf9' },
  tca:          { label: 'TCA',          color: '#6a1b9a', bg: '#f3e5f5', border: '#ce93d8' },
  system_admin: { label: 'System Admin', color: '#e65100', bg: '#fff3e0', border: '#ffcc80' },
  attender:     { label: 'Attender',     color: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
};

const ROLE_ORDER = ['invigilator', 'tca', 'system_admin', 'attender'];

const normalizeId = (val) => (val === null || val === undefined) ? '' : String(val).trim();

const formatShiftDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const AttendanceMarking = () => {
  const [loading, setLoading]               = useState(false);
  const [schedules, setSchedules]           = useState([]);
  const [shifts, setShifts]                 = useState([]);
  const [filteredShifts, setFilteredShifts] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState('');
  const [selectedShift, setSelectedShift]   = useState('');
  const [invigilators, setInvigilators]     = useState([]);
  const [attendance, setAttendance]         = useState({});
  const [alreadySaved, setAlreadySaved]     = useState(false);
  const [savedShifts, setSavedShifts]       = useState(new Set());
  const [availableMembers, setAvailableMembers] = useState({});
  const [selectedSubstitutes, setSelectedSubstitutes] = useState({}); // { mapping_id: substitute user obj }
  const [allUsers, setAllUsers]             = useState([]);

  const { user } = useAuth();
  const role = (user?.role || user?.role_name || '').toLowerCase().trim();
  const canMarkAttendance = ['super_admin', 'super admin', 'system_admin', 'system admin', 'tca'].includes(role);

  useEffect(() => {
    if (canMarkAttendance) {
      fetchSchedules();
      fetchShifts();
      fetchAllUsers();
    }
  }, [canMarkAttendance]);

  const fetchSchedules = async () => {
    try { const r = await axios.get('/schedules'); setSchedules(r.data || []); }
    catch { toast.error('Failed to load schedules'); }
  };

  const fetchShifts = async () => {
    try { const r = await axios.get('/shifts'); setShifts(r.data || []); }
    catch { toast.error('Failed to load shifts'); }
  };

  const fetchAllUsers = async () => {
    try {
      const r = await axios.get('/users');
      const filtered = (r.data || []).filter(u =>
        ['invigilator', 'tca', 'system_admin', 'attender', 'faculty'].includes(
          (u.role_name || u.role || '').toLowerCase()
        )
      );
      setAllUsers(filtered);
    } catch { toast.error('Failed to load users'); }
  };

  const fetchShiftInvigilators = async (shiftId) => {
    try {
      const r = await axios.get(`/invigilators/invigilator-mapping/${shiftId}`);
      const data = r.data || [];
      setInvigilators(data);

      const init = {};
      data.forEach(inv => { init[inv.mapping_id] = Boolean(inv.is_present); });
      setAttendance(init);

      const backendSays = data.length > 0 &&
        data.every(inv => inv.attendance_submitted === 1 || inv.attendance_submitted === true);
      const sessionSays = savedShifts.has(String(shiftId));
      setAlreadySaved(backendSays || sessionSays);

      setAvailableMembers({});
      setSelectedSubstitutes({});
    } catch { toast.error('Failed to load invigilators'); }
  };

  const getAvailableSubstitutes = (roleKey, currentInvigilators) => {
    const assignedUserIds = new Set(
      currentInvigilators.map(inv =>
        normalizeId(inv.user_id ?? inv.userId ?? inv.faculty_id ?? inv.invigilator_id ?? inv.id ?? null)
      ).filter(Boolean)
    );
    return allUsers.filter(u => {
      const uRole = (u.role_name || u.role || '').toLowerCase();
      const uId   = normalizeId(u.id);
      return uRole === roleKey && !assignedUserIds.has(uId);
    });
  };

  const handleScheduleChange = (e) => {
    const id = e.target.value;
    setSelectedSchedule(id);
    setSelectedShift('');
    setInvigilators([]);
    setAttendance({});
    setAlreadySaved(false);
    setAvailableMembers({});
    setSelectedSubstitutes({});
    setFilteredShifts(!id ? [] :
      shifts.filter(s => normalizeId(s.schedule_id) === normalizeId(id))
    );
  };

  const handleShiftChange = (e) => {
    const id = e.target.value;
    setSelectedShift(id);
    setInvigilators([]);
    setAttendance({});
    setAlreadySaved(false);
    setAvailableMembers({});
    setSelectedSubstitutes({});
    if (id) fetchShiftInvigilators(id);
  };

  const handleToggle = (mappingId, roleKey) => {
    const newValue = !attendance[mappingId];
    setAttendance(prev => ({ ...prev, [mappingId]: newValue }));

    if (!newValue) {
      const substitutes = getAvailableSubstitutes(roleKey, invigilators);
      setAvailableMembers(prev => ({ ...prev, [mappingId]: substitutes }));
    } else {
      // Marked back present — clear substitute panel and any selected substitute
      setAvailableMembers(prev => {
        const updated = { ...prev };
        delete updated[mappingId];
        return updated;
      });
      setSelectedSubstitutes(prev => {
        const updated = { ...prev };
        delete updated[mappingId];
        return updated;
      });
    }
  };

  const handleSelectSubstitute = (mappingId, mem) => {
    setSelectedSubstitutes(prev => {
      // If same substitute clicked again, deselect
      if (prev[mappingId] && normalizeId(prev[mappingId].id) === normalizeId(mem.id)) {
        const updated = { ...prev };
        delete updated[mappingId];
        return updated;
      }
      return { ...prev, [mappingId]: mem };
    });
  };

  const handleSubmit = async () => {
    if (alreadySaved) {
      toast.warning('Attendance for this shift has already been saved. It can only be marked once.');
      return;
    }
    try {
      setLoading(true);
      const payload = invigilators.map(inv => ({
        mapping_id: inv.mapping_id,
        is_present: attendance[inv.mapping_id] ? 1 : 0,
        substitute_id: !attendance[inv.mapping_id] && selectedSubstitutes[inv.mapping_id]
          ? selectedSubstitutes[inv.mapping_id].id
          : null
      }));
      await axios.put('/attendance/mark-attendance', { attendance: payload });
      toast.success('Attendance saved successfully');
      setSavedShifts(prev => new Set([...prev, String(selectedShift)]));
      setAlreadySaved(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save attendance');
    } finally {
      setLoading(false);
    }
  };

  const groupedByRole = ROLE_ORDER.reduce((acc, r) => {
    acc[r] = invigilators.filter(inv => (inv.role_name || '').toLowerCase() === r);
    return acc;
  }, {});

  const getRoleRate = (inv) => {
    const rateMap = {
      invigilator:  inv.rate_invigilator,
      tca:          inv.rate_tca,
      system_admin: inv.rate_system_admin,
      attender:     inv.rate_attender,
    };
    return Number(rateMap[inv.role_name] || inv.remuneration_rate || 0);
  };

  if (!canMarkAttendance) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '14px', maxWidth: '500px', margin: 'auto', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
          <h2 style={{ color: 'red' }}>Access Denied</h2>
          <p style={{ color: '#666' }}>You do not have permission to access Attendance Marking.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px', background: '#f5f6fa', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ color: '#1a237e', marginBottom: '8px' }}>Attendance Marking</h2>
        <p style={{ color: '#666' }}>Mark attendance by role for assigned staff</p>
      </div>

      {/* Schedule + Shift Select */}
      <div style={{ ...cardStyle, marginBottom: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <label style={labelStyle}>Select Schedule *</label>
            <select value={selectedSchedule} onChange={handleScheduleChange}
              style={{ ...inputStyle, backgroundColor: 'white' }}>
              <option value="">-- Select Schedule --</option>
              {schedules.map(s => <option key={s.id} value={s.id}>{s.exam_name}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Select Shift *</label>
            <select value={selectedShift} onChange={handleShiftChange}
              disabled={!selectedSchedule}
              style={{ ...inputStyle, backgroundColor: selectedSchedule ? 'white' : '#f0f0f0' }}>
              <option value="">{selectedSchedule ? '-- Select Shift --' : 'Select a schedule first'}</option>
              {filteredShifts.map(shift => (
                <option key={shift.id} value={shift.id}>
                  {shift.shift_name} — {formatShiftDate(shift.shift_date)}
                  {shift.start_time ? ` (${shift.start_time} - ${shift.end_time})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Already Saved Banner */}
      {alreadySaved && invigilators.length > 0 && (
        <div style={{
          marginBottom: '16px', padding: '12px 18px', borderRadius: '8px',
          background: '#fff8e1', border: '1px solid #ffe082', color: '#f57f17',
          fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          ⚠️ Attendance for this shift has already been saved. Re-submission is not allowed.
        </div>
      )}

      {/* Attendance by Role */}
      <div style={cardStyle}>
        <h3 style={{ color: '#1a237e', marginBottom: '20px' }}>Assigned Staff</h3>

        {invigilators.length === 0 ? (
          <p style={{ color: '#777' }}>
            {!selectedSchedule ? 'Please select a schedule.' :
             !selectedShift    ? 'Please select a shift.' :
                                 'No staff assigned for this shift.'}
          </p>
        ) : (
          <>
            {ROLE_ORDER.map(roleKey => {
              const group = groupedByRole[roleKey];
              const cfg   = ROLE_CONFIG[roleKey];
              if (!group || group.length === 0) return null;

              const presentCount = group.filter(inv => attendance[inv.mapping_id]).length;

              return (
                <div key={roleKey} style={{ marginBottom: '28px' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    marginBottom: '14px', padding: '10px 14px',
                    background: cfg.bg, borderRadius: '8px', border: `1px solid ${cfg.border}`
                  }}>
                    <span style={{ fontWeight: 700, color: cfg.color, fontSize: '14px' }}>
                      {cfg.label}s
                    </span>
                    <span style={{
                      fontSize: '12px', padding: '2px 10px', borderRadius: '20px',
                      background: 'white', border: `1px solid ${cfg.border}`, color: cfg.color, fontWeight: 600
                    }}>
                      {presentCount}/{group.length} present
                    </span>
                    <div style={{ flex: 1, height: '1px', background: cfg.border }} />
                  </div>

                  {group.map(inv => {
                    const isPresent    = attendance[inv.mapping_id];
                    const rate         = getRoleRate(inv);
                    const available    = availableMembers[inv.mapping_id];
                    const hasPanel     = available !== undefined;
                    const selectedSub  = selectedSubstitutes[inv.mapping_id];

                    return (
                      <div key={inv.mapping_id} style={{ marginBottom: '10px' }}>
                        <div style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '14px 16px',
                          border: `1px solid ${isPresent ? cfg.border : '#ffcdd2'}`,
                          borderRadius: hasPanel ? '10px 10px 0 0' : '10px',
                          background: isPresent ? cfg.bg : '#fff8f8'
                        }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <h4 style={{ color: '#1a237e', margin: 0 }}>{inv.name}</h4>
                              <span style={{
                                fontSize: '11px', fontWeight: 600, padding: '2px 8px',
                                borderRadius: '12px', background: cfg.bg,
                                color: cfg.color, border: `1px solid ${cfg.border}`
                              }}>{cfg.label}</span>
                            </div>
                            <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>{inv.email}</p>
                            {rate > 0 && (
                              <p style={{ margin: '4px 0 0', color: '#2e7d32', fontSize: '12px', fontWeight: 600 }}>
                                Rate: ₹{rate.toFixed(2)} {isPresent ? `→ Earns ₹${rate.toFixed(2)}` : '→ ₹0.00 (absent)'}
                              </p>
                            )}
                            {/* Show selected substitute under absent person */}
                            {!isPresent && selectedSub && (
                              <p style={{ margin: '4px 0 0', color: '#1565c0', fontSize: '12px', fontWeight: 600 }}>
                                🔄 Substitute: {selectedSub.name}
                              </p>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ color: isPresent ? '#2e7d32' : '#e53935', fontWeight: 600 }}>
                              {isPresent ? 'Present' : 'Absent'}
                            </span>
                            <button
                              onClick={() => handleToggle(inv.mapping_id, roleKey)}
                              disabled={alreadySaved}
                              style={{
                                padding: '8px 16px', border: 'none', borderRadius: '8px',
                                cursor: alreadySaved ? 'not-allowed' : 'pointer', fontWeight: 600,
                                background: alreadySaved ? '#bdbdbd' : isPresent ? '#e53935' : '#43a047',
                                color: 'white', opacity: alreadySaved ? 0.7 : 1
                              }}>
                              {isPresent ? 'Mark Absent' : 'Mark Present'}
                            </button>
                          </div>
                        </div>

                        {/* Substitutes Panel */}
                        {!isPresent && hasPanel && (
                          <div style={{
                            border: `1px solid #ffcdd2`, borderTop: 'none',
                            borderRadius: '0 0 10px 10px', background: '#fff3f3',
                            padding: '12px 16px'
                          }}>
                            <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#c62828', fontSize: '13px' }}>
                              Available {cfg.label} substitutes — click to select:
                            </p>
                            {available.length === 0 ? (
                              <p style={{ margin: 0, color: '#888', fontSize: '13px' }}>
                                No available substitutes found.
                              </p>
                            ) : (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {available.map(mem => {
                                  const isSelected = selectedSub && normalizeId(selectedSub.id) === normalizeId(mem.id);
                                  return (
                                    <div
                                      key={mem.id}
                                      onClick={() => handleSelectSubstitute(inv.mapping_id, mem)}
                                      style={{
                                        padding: '6px 12px', borderRadius: '8px', cursor: 'pointer',
                                        background: isSelected ? cfg.bg : 'white',
                                        border: isSelected ? `2px solid ${cfg.color}` : `1px solid ${cfg.border}`,
                                        fontSize: '13px', color: '#333',
                                        boxShadow: isSelected ? `0 0 0 2px ${cfg.border}` : 'none',
                                        transition: '0.15s'
                                      }}>
                                      <span style={{ fontWeight: 600, color: cfg.color }}>{mem.name}</span>
                                      {mem.email && (
                                        <span style={{ color: '#888', marginLeft: '6px' }}>{mem.email}</span>
                                      )}
                                      {isSelected && (
                                        <span style={{ marginLeft: '6px', color: cfg.color, fontWeight: 700 }}>✓</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            <button onClick={handleSubmit} disabled={loading || alreadySaved} style={{
              marginTop: '10px', width: '100%', padding: '14px',
              background: alreadySaved ? '#9e9e9e' : '#1a237e',
              color: 'white', border: 'none', borderRadius: '10px',
              fontSize: '16px', fontWeight: '600',
              cursor: loading || alreadySaved ? 'not-allowed' : 'pointer',
              opacity: alreadySaved ? 0.7 : 1
            }}>
              {loading ? 'Saving...' : alreadySaved ? 'Already Saved' : 'Save Attendance'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AttendanceMarking;